import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import states from '../../../../components/common/states'; // adjust path as needed
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SearchableDropdown from '../../../../components/common/SearchableDropdown';

const emptyCommodityEntry = {
  commodity_id: '',
  commission_type: 'PERCENT',
  commission_value: '',
  state: '',
  state_code: '',
  effective_date: ''
};

const SaleBrokerForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    contact_phone: '',
    address: '',
    gst_no: '',
    aadhar_no: '',
    pan_no: '',
    email: '',
    bank_name: '',
    bank_address: '',
    account_no: '',
    ifsc_code: '',
    commodity_entries: [{ ...emptyCommodityEntry }]
  });
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    contact_phone: '',
    address: '',
    gst_no: '',
    aadhar_no: '',
    pan_no: '',
    email: '',
    bank_name: '',
    bank_address: '',
    account_no: '',
    ifsc_code: '',
    commodity_entries: []
  });
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const navigate = useNavigate();

  // Fetch commodities list
  useEffect(() => {
    const fetchCommodities = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/commodity-get`, {
          credentials: 'include',
        });
        if (res.ok) {
          const result = await res.json();
          setCommodities(result.data || []);
        }
      } catch (error) {
        console.error('Error fetching commodities:', error);
      }
    };
    fetchCommodities();
  }, []);

  // Fetch sale broker data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching sale broker with ID:', id);
      const fetchSaleBroker = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/sale-broker-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch sale broker');
          }
          const result = await res.json();
          console.log('Fetched sale broker data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            const newFormData = {
              name: result.data.name ?? '',
              contact_phone: result.data.contact_phone ?? '',
              address: result.data.address ?? '',
              gst_no: result.data.gst_no ?? '',
              aadhar_no: result.data.aadhar_no ?? '',
              pan_no: result.data.pan_no ?? '',
              email: result.data.email ?? '',
              bank_name: result.data.bank_name ?? '',
              bank_address: result.data.bank_address ?? '',
              account_no: result.data.account_no ?? '',
              ifsc_code: result.data.ifsc_code ?? '',
              commodity_entries: result.data.commodity_entries && result.data.commodity_entries.length > 0 
                ? result.data.commodity_entries 
                : [{ ...emptyCommodityEntry }]
            };
            console.log('Setting new form data:', newFormData);
            setFormData(newFormData);
            setOriginalFormData(newFormData); // Store original values for read-only check
          } else {
            toast.error('Sale Broker not found');
            navigate('/broker');
          }
        } catch (error) {
          console.error('Error fetching sale broker:', error);
          toast.error('Failed to load sale broker data');
          navigate('/broker');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchSaleBroker();
    }
  }, [id, isEditMode, navigate]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('FormData changed:', formData);
  }, [formData]);

  const FIELD_LIMITS = {
    name: 100,
    commission_type: 10,
    commission_value: 10, // decimal(10,2) - input type number, so not needed for maxLength
    contact_phone: 20,
    address: 255,
    gst_no: 20,
    state: 100,
    state_code: 10,
    aadhar_no: 20,
    pan_no: 20,
    email: 100,
    bank_name: 100,
    bank_address: 255,
    account_no: 30,
    ifsc_code: 20,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields
    const requiredFields = ['name', 'commission_type', 'commission_value', 'contact_phone', 'address', 'gst_no', 'state', 'account_no', 'email', 'bank_name'];
    if (requiredFields.includes(name) && (!value || value.trim() === '')) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Check if field is read-only (has existing value from database)
    const isReadOnly = (fieldName) => {
      // Only check if this is edit mode and the field had an original value
      if (!isEditMode) return false;
      
      // Get the original value from the database (stored separately)
      const originalValue = originalFormData[fieldName];
      return originalValue && originalValue.toString().trim() !== '';
    };
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'commission_value' ? value.replace(/[^0-9.]/g, '') : value
    }));
  };

  const handleCommodityEntryChange = (idx, fieldName, value) => {
    setFormData(prev => {
      const updatedEntries = [...prev.commodity_entries];
      updatedEntries[idx] = {
        ...updatedEntries[idx],
        [fieldName]: value
      };
      
      // Auto-update state_code when state changes
      if (fieldName === 'state') {
        const matched = states.find(s => s.name === value);
        updatedEntries[idx].state_code = matched ? matched.code : '';
      }
      
      return {
        ...prev,
        commodity_entries: updatedEntries
      };
    });
  };

  const addCommodityEntry = () => {
    setFormData(prev => ({
      ...prev,
      commodity_entries: [...prev.commodity_entries, { ...emptyCommodityEntry }]
    }));
  };

  const removeCommodityEntry = (idx) => {
    if (formData.commodity_entries.length > 1) {
      setFormData(prev => ({
        ...prev,
        commodity_entries: prev.commodity_entries.filter((_, i) => i !== idx)
      }));
    } else {
      toast.error('At least one commodity entry is required');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    // Validation: check required fields
    const requiredFields = ['name', 'contact_phone', 'address', 'gst_no', 'account_no', 'email', 'bank_name'];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }

    // Validate commodity entries
    if (!formData.commodity_entries || formData.commodity_entries.length === 0) {
      toast.error('At least one commodity entry is required');
      setLoading(false);
      return;
    }

    for (let i = 0; i < formData.commodity_entries.length; i++) {
      const entry = formData.commodity_entries[i];
      if (!entry.commodity_id) {
        toast.error(`Commodity is required for entry ${i + 1}`);
        setLoading(false);
        return;
      }
      if (!entry.commission_type || !entry.commission_value) {
        toast.error(`Commission Type and Value are required for entry ${i + 1}`);
        setLoading(false);
        return;
      }
      if (!entry.state) {
        toast.error(`State is required for entry ${i + 1}`);
        setLoading(false);
        return;
      }
    }
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/sale-broker-put/${id}`
        : `${API_BASE_URL}/api/masters/sale-broker-post`;
      
      console.log('=== SUBMITTING SALE BROKER DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', formData);
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'Sale Broker Updated Successfully!' : 'Broker Registered Successfully!');
        if (!isEditMode) {
          setFormData({
            name: '',
            contact_phone: '',
            address: '',
            gst_no: '',
            aadhar_no: '',
            pan_no: '',
            email: '',
            bank_name: '',
            bank_address: '',
            account_no: '',
            ifsc_code: '',
            commodity_entries: [{ ...emptyCommodityEntry }]
          });
        } else {
          navigate('/broker');
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-bold mb-0 text-left">{isEditMode ? 'Edit Sale Broker' : 'Sale Broker Entry'}</h2>
              {isEditMode && (
                <div className="text-xs text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

            {/* Basic Broker Information */}
            <div><label className="block text-xs font-medium mb-1">Broker Name</label><input type="text" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.name} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required /></div>
            <div><label className="block text-xs font-medium mb-1">Contact Phone</label><input type="text" name="contact_phone" value={formData.contact_phone} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.contact_phone} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required /></div>
            <div><label className="block text-xs font-medium mb-1">Address</label><input type="text" name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.address} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required /></div>
            <div><label className="block text-xs font-medium mb-1">GST No</label><input type="text" name="gst_no" value={formData.gst_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.gst_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required /></div>
            <div><label className="block text-xs font-medium mb-1">Aadhar No</label><input type="text" name="aadhar_no" value={formData.aadhar_no} onChange={handleChange} maxLength={FIELD_LIMITS.aadhar_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" /></div>
            <div><label className="block text-xs font-medium mb-1">PAN No</label><input type="text" name="pan_no" value={formData.pan_no} onChange={handleChange} maxLength={FIELD_LIMITS.pan_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" /></div>
            <div><label className="block text-xs font-medium mb-1">Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.email} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required /></div>
            <div><label className="block text-xs font-medium mb-1">Bank Name</label><input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.bank_name} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required /></div>
            <div><label className="block text-xs font-medium mb-1">Bank Address</label><input type="text" name="bank_address" value={formData.bank_address} onChange={handleChange} maxLength={FIELD_LIMITS.bank_address} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" /></div>
            <div><label className="block text-xs font-medium mb-1">Account No</label><input type="text" name="account_no" value={formData.account_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.account_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required /></div>
            <div><label className="block text-xs font-medium mb-1">IFSC Code</label><input type="text" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} maxLength={FIELD_LIMITS.ifsc_code} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" /></div>

          </div>
        </div>

        {/* Commodity Entries Section */}
        <div className="px-4 pt-4 pb-2 border-t border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Commodity & Commission Details</h3>
          </div>
          {formData.commodity_entries.map((entry, idx) => (
            <div key={idx} className="border border-gray-300 p-4 mb-3 rounded relative bg-gray-50">
              {formData.commodity_entries.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCommodityEntry(idx)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-xs flex items-center gap-1"
                >
                  <FaTrash /> Remove
                </button>
              )}
              <div className="text-xs font-medium text-gray-600 mb-2">Entry #{idx + 1}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Commodity</label>
                  <SearchableDropdown
                    options={commodities}
                    value={entry.commodity_id}
                    onChange={(value) => handleCommodityEntryChange(idx, 'commodity_id', value)}
                    placeholder="Select Commodity"
                    displayKey="name"
                    valueKey="commodity_id"
                    customDisplay={(item) => `${item.name}${item.origin ? ` (${item.origin})` : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Commission Type</label>
                  <select
                    value={entry.commission_type}
                    onChange={(e) => handleCommodityEntryChange(idx, 'commission_type', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                  >
                    <option value="PERCENT">PERCENT</option>
                    <option value="FLAT">FLAT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Commission Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={entry.commission_value}
                    onChange={(e) => handleCommodityEntryChange(idx, 'commission_value', e.target.value.replace(/[^0-9.]/g, ''))}
                    className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">State</label>
                  <select
                    value={entry.state}
                    onChange={(e) => handleCommodityEntryChange(idx, 'state', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                    required
                  >
                    <option value="">-- Select State --</option>
                    {states.map((s, i) => (
                      <option key={i} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">State Code</label>
                  <input
                    type="text"
                    value={entry.state_code}
                    readOnly
                    maxLength={10}
                    className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none focus:border-[#10a5e9] rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Effective Date</label>
                  <input
                    type="date"
                    value={entry.effective_date}
                    onChange={(e) => handleCommodityEntryChange(idx, 'effective_date', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addCommodityEntry}
            className="flex items-center gap-1 bg-gray-200 text-gray-800 px-3 py-1.5 text-xs rounded hover:bg-gray-300 border border-gray-300 font-medium"
          >
            <FaPlus /> Add More Commodity Entry
          </button>
        </div>

        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#10a5e9] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Sale Broker' : 'Save Broker')}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default SaleBrokerForm;
